import React from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ICellRendererParams } from "@ag-grid-community/core";

import EmptyState from "fe-shared/src/components/EmptyState";
import { InboxIcon } from "fe-shared/src/components/Icons";

import { PickupRequestPriority, PickupRequestStatus } from "../../../types";

import {
  RequestNumberRenderer,
  ParticipantsGridCell,
  StatusGridCell,
  HaulerCompanyGridCell,
  CollectorCompanyGridCell,
  CarrierGridCell,
  RequestWarningsGridCell,
  SubmittedDateGridCell,
  DateRangeGridCell,
} from "../renderers";

// fe-internal-requests' Requests.tsx and its only-mine filter both address this column by id;
// AG Grid derives the id from `field`, so renaming the field without them silently disables both.
export const ASSIGNEE_COL_ID = "assignedToFullName";

export const PRIORITY_COL_ID = "priorityCode";

export const getPortalColumnDefs = (t: TFunction) => [
  {
    headerName: t("pickupRequests.grid.cols.submittedOn"),
    field: "submittedDate",
    minWidth: 200,
    filter: "dateFilter",
    cellRenderer: "submittedDateRenderer",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.requestNumber"),
    field: "requestNumber",
    minWidth: 160,
    cellRenderer: "requestNumberRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.participants"),
    colId: "participants",
    minWidth: 220,
    flex: 1,
    cellRenderer: "participantsRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.municipality"),
    field: "municipalityName",
    minWidth: 180,
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.zone"),
    field: "zoneName",
    minWidth: 140,
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.priority"),
    field: PRIORITY_COL_ID,
    minWidth: 160,
    cellRenderer: "priorityRenderer",
    filter: "selectFilter",
    floatingFilterComponent: "multiSelectFilter",
    floatingFilterComponentParams: {
      id: "priority-multi-select-filter",
      suppressFilterButton: true,
      options: Object.values(PickupRequestPriority).map((item) => ({
        value: item,
        label: t(`pickupRequests.priorities.${item}`, { defaultValue: item }),
      })),
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.createdBy"),
    field: "createdByFullName",
    minWidth: 180,
    cellRenderer: "createdByRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.pickupDate"),
    colId: "pickupDate",
    minWidth: 220,
    cellRenderer: "pickupDateRenderer",
    filter: "dateFilter",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      isFutureEnabled: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.status"),
    field: "statusCode",
    minWidth: 160,
    cellRenderer: "statusRenderer",
    filter: "selectFilter",
    floatingFilterComponent: "multiSelectFilter",
    floatingFilterComponentParams: {
      id: "status-multi-select-filter",
      suppressFilterButton: true,
      options: Object.values(PickupRequestStatus).map((item) => ({
        value: item,
        label: t(`pickupRequests.statuses.${item}`, { defaultValue: item }),
      })),
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.assignedTo"),
    field: ASSIGNEE_COL_ID,
    minWidth: 200,
    cellRenderer: "assigneeRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: "",
    sortable: false,
    colId: "warning",
    cellRenderer: "warningRenderer",
    filter: "textFilter",
    floatingFilterComponent: "clearFilters",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      id: "request-clear-filters-button",
    },
    width: 80,
    minWidth: 80,
  },
];

export const getCollectorColumnDefs = (t: TFunction) => [
  {
    headerName: t("pickupRequests.grid.cols.submittedOn"),
    field: "submittedDate",
    minWidth: 200,
    filter: "dateFilter",
    cellRenderer: "submittedDateRenderer",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.requestNumber"),
    field: "requestNumber",
    minWidth: 160,
    cellRenderer: "requestNumberRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.hauler"),
    field: "targetCompanyName",
    minWidth: 220,
    flex: 1,
    // Request.GetPickupRequests has no order-by branch for TargetCompanyName; leaving the grid
    // default sortable would paint an arrow and silently re-sort by SubmittedDate instead.
    sortable: false,
    cellRenderer: "haulerRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.pickupDate"),
    colId: "pickupDate",
    minWidth: 220,
    cellRenderer: "pickupDateRenderer",
    filter: "dateFilter",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      isFutureEnabled: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.status"),
    field: "statusCode",
    minWidth: 160,
    cellRenderer: "statusRenderer",
    filter: "selectFilter",
    floatingFilterComponent: "multiSelectFilter",
    floatingFilterComponentParams: {
      id: "status-multi-select-filter",
      suppressFilterButton: true,
      options: Object.values(PickupRequestStatus).map((item) => ({
        value: item,
        label: t(`pickupRequests.statuses.${item}`, { defaultValue: item }),
      })),
    },
  },
  {
    headerName: "",
    sortable: false,
    colId: "warning",
    cellRenderer: "warningRenderer",
    filter: "textFilter",
    floatingFilterComponent: "clearFilters",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      id: "collector-request-clear-filters-button",
    },
    width: 80,
  },
];

const EmptyGridView = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      iconComponent={InboxIcon}
      title={t("pickupRequests.grid.empty.title")}
      subtitle={t("pickupRequests.grid.empty.subtitle")}
    />
  );
};

export const getHaulerColumnDefs = (t: TFunction) => [
  {
    headerName: t("pickupRequests.grid.cols.submittedOn"),
    field: "submittedDate",
    minWidth: 180,
    filter: "dateFilter",
    cellRenderer: "submittedDateRenderer",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.requestNumber"),
    field: "requestNumber",
    minWidth: 150,
    cellRenderer: "requestNumberRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.pickUpFrom"),
    field: "sourceCompanyName",
    minWidth: 220,
    flex: 1,
    cellRenderer: "collectorRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.municipality"),
    field: "municipalityName",
    minWidth: 180,
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.zone"),
    field: "zoneName",
    minWidth: 140,
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.carrier"),
    field: "carrierFirstName",
    minWidth: 180,
    // Request.GetPickupRequests has no order-by branch for the carrier; it filters on the two name
    // columns concatenated, which is why the filter maps to CarrierName rather than to this field.
    sortable: false,
    cellRenderer: "carrierRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("pickupRequests.grid.filterPlaceholder"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.pickupDate"),
    colId: "pickupDate",
    minWidth: 220,
    cellRenderer: "pickupDateRenderer",
    filter: "dateFilter",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      isFutureEnabled: true,
    },
  },
  {
    headerName: t("pickupRequests.grid.cols.status"),
    field: "statusCode",
    minWidth: 160,
    cellRenderer: "statusRenderer",
    filter: "selectFilter",
    floatingFilterComponent: "multiSelectFilter",
    floatingFilterComponentParams: {
      id: "hauler-status-multi-select-filter",
      suppressFilterButton: true,
      options: Object.values(PickupRequestStatus).map((item) => ({
        value: item,
        label: t(`pickupRequests.statuses.${item}`, { defaultValue: item }),
      })),
    },
  },
  {
    headerName: "",
    sortable: false,
    colId: "warning",
    cellRenderer: "warningRenderer",
    filter: "textFilter",
    floatingFilterComponent: "clearFilters",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      id: "hauler-request-clear-filters-button",
    },
    width: 80,
  },
];

export const haulerRenderers = {
  submittedDateRenderer: SubmittedDateGridCell,
  requestNumberRenderer: RequestNumberRenderer,
  collectorRenderer: CollectorCompanyGridCell,
  carrierRenderer: CarrierGridCell,
  pickupDateRenderer: DateRangeGridCell,
  statusRenderer: StatusGridCell,
  warningRenderer: (props: ICellRendererParams) =>
    RequestWarningsGridCell({ data: props.data, showCarrierWarning: true }),
  emptyGridView: EmptyGridView,
};

export const collectorRenderers = {
  submittedDateRenderer: SubmittedDateGridCell,
  requestNumberRenderer: RequestNumberRenderer,
  haulerRenderer: HaulerCompanyGridCell,
  pickupDateRenderer: DateRangeGridCell,
  statusRenderer: StatusGridCell,
  warningRenderer: RequestWarningsGridCell,
  emptyGridView: EmptyGridView,
};

export const portalRenderers = {
  submittedDateRenderer: SubmittedDateGridCell,
  requestNumberRenderer: RequestNumberRenderer,
  participantsRenderer: ParticipantsGridCell,
  pickupDateRenderer: DateRangeGridCell,
  statusRenderer: StatusGridCell,
};
